import React, { useEffect, useRef, useState } from 'react';
import { Form, Card, ListGroup, Button, Modal, Spinner } from 'react-bootstrap';
import BOMDataDisplay from './BOMDataDisplay';

const FileUpload = ({
  onFileSelect,
  onUploadCSV,
  csvData,
  bomData,
  supportedFactoryCodes,
  factoryBOMRows,
  isLoading,
  resetTrigger,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadToast, setUploadToast] = useState({ show: false, message: '', type: 'error' });
  const [isDragOver, setIsDragOver] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmittingUpload, setIsSubmittingUpload] = useState(false);
  const [uploadResult, setUploadResult] = useState({
    status: 'idle',
    missingTwelveNCs: [],
    withoutProductFamilyTwelveNCs: [],
    invalidServiceTags: [],
    errorMessage: '',
  });
  const fileInputRef = useRef(null);
  const dragDepthRef = useRef(0);

  const dismissUploadToast = () => {
    setUploadToast({ show: false, message: '', type: 'error' });
  };

  const dismissErrorToastOnAction = () => {
    if (uploadToast.show && uploadToast.type === 'error') {
      dismissUploadToast();
    }
  };

  useEffect(() => {
    setSelectedFiles([]);
    setUploadToast({ show: false, message: '', type: 'error' });
    setCurrentStep(1);
    setUploadResult({
      status: 'idle',
      missingTwelveNCs: [],
      withoutProductFamilyTwelveNCs: [],
      invalidServiceTags: [],
      errorMessage: '',
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [resetTrigger]);

  useEffect(() => {
    if (!uploadToast.show) {
      return;
    }

    const timer = setTimeout(() => {
      setUploadToast({ show: false, message: '', type: 'error' });
    }, 5000);

    return () => clearTimeout(timer);
  }, [uploadToast.show]);

  const normalizeSelectedFiles = (files) => {
    const invalidMessages = [];
    const validFiles = [];
    const fileList = files ? Array.from(files) : [];

    for (const file of fileList) {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        invalidMessages.push(`File "${file.name}" is not a CSV file. Please select only CSV files.`);
        continue;
      }

      if (file.size > 40 * 1024 * 1024) {
        invalidMessages.push(`File "${file.name}" exceeds 40MB size limit.`);
        continue;
      }

      validFiles.push(file);
    }

    return { validFiles, invalidMessages };
  };

  const handleFileChange = (event) => {
    dismissErrorToastOnAction();
    const { validFiles, invalidMessages } = normalizeSelectedFiles(event.target.files);

    if (validFiles.length > 0) {
      setSelectedFiles(validFiles.slice(0, 1));
      setCurrentStep(1);
    } else {
      setSelectedFiles([]);
    }

    if (invalidMessages.length > 0) {
      setUploadToast({
        show: true,
        message: invalidMessages.join(' '),
        type: 'error',
      });
    }

    if (event.target) {
      event.target.value = '';
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    dismissErrorToastOnAction();
    dragDepthRef.current = 0;
    setIsDragOver(false);

    const { validFiles, invalidMessages } = normalizeSelectedFiles(event.dataTransfer.files);
    if (validFiles.length > 0) {
      setSelectedFiles(validFiles.slice(0, 1));
      setCurrentStep(1);
    }

    if (invalidMessages.length > 0) {
      setUploadToast({
        show: true,
        message: invalidMessages.join(' '),
        type: 'error',
      });
    }
  };

  const removeFile = (index) => {
    dismissErrorToastOnAction();
    setSelectedFiles([]);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragOver(false);
    }
  };

  const handleAreaClick = () => {
    dismissErrorToastOnAction();
    if (!isLoading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAreaKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleAreaClick();
    }
  };

  const handleOpenModal = () => {
    dismissErrorToastOnAction();
    setShowModal(true);
    setCurrentStep(1);
  };

  const handleCloseModal = () => {
    dismissErrorToastOnAction();

    // Always clear the selected file and parsed data so reopening starts fresh,
    // even when the upload failed with an error.
    setSelectedFiles([]);
    if (typeof onFileSelect === 'function') {
      onFileSelect([]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setShowModal(false);
    setCurrentStep(1);
    setUploadResult({
      status: 'idle',
      missingTwelveNCs: [],
      withoutProductFamilyTwelveNCs: [],
      invalidServiceTags: [],
      errorMessage: '',
    });
  };

  const handlePreview = () => {
    dismissErrorToastOnAction();
    if (selectedFiles.length === 0) {
      setUploadToast({
        show: true,
        message: 'Please select a CSV file before preview.',
        type: 'error',
      });
      return;
    }

    if (onFileSelect) {
      onFileSelect(selectedFiles);
    }

    setCurrentStep(2);
  };

  const handleUpload = async () => {
    dismissErrorToastOnAction();
    if (typeof onUploadCSV !== 'function') {
      setUploadResult({
        status: 'failed',
        missingTwelveNCs: [],
        withoutProductFamilyTwelveNCs: [],
        errorMessage: 'Failed to Upload.',
      });
      setCurrentStep(3);
      return;
    }

    setIsSubmittingUpload(true);

    try {
      const response = await onUploadCSV(selectedFiles);
      const missingTwelveNCs = Array.isArray(response?.missingTwelveNCs) ? response.missingTwelveNCs : [];
      const withoutProductFamilyTwelveNCs = Array.isArray(response?.withoutProductFamilyTwelveNCs)
        ? response.withoutProductFamilyTwelveNCs
        : [];
      const invalidServiceTags = Array.isArray(response?.invalidServicetag) ? response.invalidServicetag : [];

      setUploadResult({
        status: 'success',
        missingTwelveNCs,
        withoutProductFamilyTwelveNCs,
        invalidServiceTags,
        errorMessage: '',
      });
    } catch (uploadError) {
      setUploadResult({
        status: 'failed',
        missingTwelveNCs: [],
        withoutProductFamilyTwelveNCs: [],
        invalidServiceTags: [],
        errorMessage: uploadError?.message || 'Failed to Upload.',
      });
    } finally {
      setIsSubmittingUpload(false);
      setCurrentStep(3);
    }
  };

  const selectedFileNames = selectedFiles.map((file) => file.name).slice(0, 2).join(', ');
  const hasMoreFiles = selectedFiles.length > 2;
  const hasPreviewData = Array.isArray(csvData?.originalCsvData) && csvData.originalCsvData.length > 0;
  const steps = [
    { id: 1, title: 'Upload CSV' },
    { id: 2, title: 'Preview Parsed Results' },
    { id: 3, title: 'Summary' },
  ];

  const getStepInstruction = () => {
    if (currentStep === 1) {
      return {
        heading: 'Step 1 of 3: Upload CSV',
        details: 'Provide your email and select a CSV file with one servicetag column. Then click "Generate Preview" to validate and continue.',
      };
    }

    if (currentStep === 2) {
      return {
        heading: 'Step 2 of 3: Preview Parsed Results',
        details: 'Review the summary metrics and 12NC status. Missing BOMs will be warned and logged, not silently dropped. Confirm and click Upload.',
      };
    }

    return {
      heading: 'Step 3 of 3: Confirm Upload',
      details: 'Upload completed. Review the summary below for any warnings or issues that may need follow-up.',
    };
  };

  const stepInstruction = getStepInstruction();

  const renderStepThreeContent = () => {
    if (uploadResult.status !== 'success') {
      return (
        <div className="alert alert-danger mb-0">
          <strong>Failed to Upload.</strong>
          <div className="mt-1">{uploadResult.errorMessage || 'Please try again.'}</div>
        </div>
      );
    }

    const hasMissingTwelveNCs = uploadResult.missingTwelveNCs.length > 0;
    const hasMissingProductFamilyMapping = uploadResult.withoutProductFamilyTwelveNCs.length > 0;
    const hasInvalidServiceTags = uploadResult.invalidServiceTags.length > 0;
    const totalServiceTags = csvData?.originalCsvData?.length || 0;
    const validServiceTags = csvData?.originalCsvData?.filter(row => row.isValid !== false).length || 0;

    return (
      <div>
        <div className="row mb-3">
          <div className="col-md-4 mb-2">
            <Card className="text-center h-100">
              <Card.Body style={{ padding: '8px 12px' }}>
                <small className="text-muted mb-1 d-block">File Name</small>
                <p className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>{selectedFiles[0]?.name || 'N/A'}</p>
              </Card.Body>
            </Card>
          </div>
          <div className="col-md-4 mb-2">
            <Card className="text-center h-100">
              <Card.Body style={{ padding: '8px 12px' }}>
                <small className="text-muted mb-1 d-block">Final Status</small>
                <h6 className={hasMissingTwelveNCs || hasMissingProductFamilyMapping || hasInvalidServiceTags ? 'text-warning' : 'text-success'} style={{ marginBottom: '4px' }}>
                  {hasMissingTwelveNCs || hasMissingProductFamilyMapping ? 'Completed with warnings' : 'Completed'}
                </h6>
              </Card.Body>
            </Card>
          </div>
          <div className="col-md-4 mb-2">
            <Card className="text-center h-100">
              <Card.Body style={{ padding: '8px 12px' }}>
                <small className="text-muted mb-1 d-block">Upload Time</small>
                <p className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>{new Date().toLocaleTimeString()}</p>
                <small className="text-muted" style={{ fontSize: '0.8rem' }}>{new Date().toLocaleDateString()}</small>
              </Card.Body>
            </Card>
          </div>
        </div>

        <div className="alert alert-success mb-3" style={{ backgroundColor: '#d4edda', color: '#155724', border: 'none' }}>
          <strong>Upload successful.</strong>
          {hasInvalidServiceTags && (
            <div className="mt-2 alert alert-warning" style={{ backgroundColor: '#fff3cd', color: '#856404', border: 'none', marginBottom: 0 }}>
              <strong>Invalid Service Tags ({uploadResult.invalidServiceTags.length}):</strong>
              <div className="mt-1 small">{uploadResult.invalidServiceTags.join(', ')}</div>
            </div>
          )}
          {hasMissingTwelveNCs && (
            <div className="mt-2 alert alert-warning" style={{ backgroundColor: '#fff3cd', color: '#856404', border: 'none', marginBottom: 0 }}>
              <strong>Missing 12NCs ({uploadResult.missingTwelveNCs.length}):</strong>
              <div className="mt-1 small">{uploadResult.missingTwelveNCs.join(', ')}</div>
            </div>
          )}
          {hasMissingProductFamilyMapping && (
            <div className="mt-2 alert alert-warning" style={{ backgroundColor: '#fff3cd', color: '#856404', border: 'none', marginBottom: 0 }}>
              <strong>12NC mapping with Product Family is missing ({uploadResult.withoutProductFamilyTwelveNCs.length}):</strong>
              <div className="mt-1 small">{uploadResult.withoutProductFamilyTwelveNCs.join(', ')}</div>
            </div>
          )}
          {!hasMissingTwelveNCs && !hasMissingProductFamilyMapping && !hasInvalidServiceTags && (
            <div className="mt-2 small text-success">All service tags were processed successfully.</div>
          )}
        </div>

        <Card className="mt-3">
          <Card.Header className="bg-light">
            <h6 className="mb-0">Summary</h6>
          </Card.Header>
          <Card.Body>
            <ul className="mb-0">
              <li>Total Service Tags: <strong>{totalServiceTags}</strong></li>
              <li>Valid Service Tags: <strong>{validServiceTags}</strong></li>
              <li>12NCs Processed: <strong>{new Set(csvData?.originalCsvData?.map(row => row.extracted12NC).filter(Boolean)).size}</strong></li>
              <li>Invalid Service Tags: <strong>{uploadResult.invalidServiceTags.length}</strong></li>
              <li>Missing 12NCs: <strong>{uploadResult.missingTwelveNCs.length}</strong></li>
              <li>Missing Product Family Mappings: <strong>{uploadResult.withoutProductFamilyTwelveNCs.length}</strong></li>
            </ul>
          </Card.Body>
        </Card>
      </div>
    );
  };

  return (
    <Card className="mb-4 h-100 app-action-card">
      <Card.Body className="app-action-card-body d-flex align-items-center justify-content-center">
        {uploadToast.show && (
          <div className={`floating-toast floating-toast-${uploadToast.type}`} role="alert" aria-live="assertive">
            <div className="floating-toast-content">
              <strong>Error</strong>
              <p>{uploadToast.message}</p>
              <small className="text-muted">If this message is not relevant anymore, use × to dismiss.</small>
            </div>
            <Button
              variant="link"
              className="floating-toast-close"
              onClick={dismissUploadToast}
              aria-label="Dismiss error"
              title="Dismiss this message"
            >
              ×
            </Button>
          </div>
        )}

        <Button className="bom-btn-primary" size="lg" onClick={handleOpenModal}>
          Upload Factory File
        </Button>

        <Modal show={showModal} onHide={handleCloseModal} size="lg" centered scrollable>
          <Modal.Header closeButton>
            <div>
              <Modal.Title>Upload Factory File</Modal.Title>
              <small className="text-muted">
                Upload a production CSV with one servicetag column. Missing BOMs will be warned and logged, not silently dropped.
              </small>
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-2 p-2 border rounded bg-light" style={{ fontSize: '0.9rem' }}>
              <div className="d-flex align-items-center mb-2">
                {steps.map((step, index) => {
                  const isActive = currentStep === step.id;
                  const isCompleted = currentStep > step.id;

                  return (
                    <React.Fragment key={step.id}>
                      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <div
                          className="d-flex align-items-center gap-2"
                          style={isActive ? {
                            padding: '8px 16px',
                            borderRadius: '12px',
                            background: 'rgba(255, 255, 255, 0.5)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.8)',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          } : {}}
                        >
                          <span
                            className={`badge ${isActive ? 'bg-primary' : isCompleted ? 'bg-success' : 'bg-secondary'}`}
                          >
                            {step.id}
                          </span>
                          <span className={isActive ? 'fw-bold text-primary' : 'text-muted'}>{step.title}</span>
                        </div>
                      </div>
                      {index < steps.length - 1 && (
                        <div style={{ flex: 0.3, display: 'flex', justifyContent: 'center' }}>
                          <span className="text-muted">→</span>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {currentStep === 1 && (
              <div className="row">
                <div className="col-md-8">

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Factory CSV</Form.Label>
                    <div
                      className={`upload-area p-4 border-2 border-dashed rounded text-center ${isDragOver ? 'drag-over' : ''}`}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onClick={handleAreaClick}
                      onKeyDown={handleAreaKeyDown}
                      role="button"
                      tabIndex={isLoading ? -1 : 0}
                      aria-label="Upload CSV files"
                      style={{
                        borderColor: '#dee2e6',
                        backgroundColor: '#f8f9fa',
                        cursor: 'pointer',
                      }}
                    >
                      <Form.Group>
                        <div className="fw-bold mb-2">
                          {isDragOver ? 'Drop files to upload' : 'Drag files here or click to upload'}
                        </div>
                        {selectedFiles.length > 0 && (
                          <div className="mb-2 py-2 text-success fw-semibold">
                            Selected file: {selectedFiles[0].name}
                          </div>
                        )}
                        <Form.Control
                          ref={fileInputRef}
                          type="file"
                          accept=".csv"
                          onChange={handleFileChange}
                          disabled={isLoading}
                          className="d-none"
                        />
                      </Form.Group>
                    </div>
                    <small className="d-block mt-2 text-muted">
                      Selected file: {selectedFiles.length > 0 ? selectedFiles[0].name : 'No file selected'}
                    </small>
                  </Form.Group>

                  {selectedFiles.length > 0 && (
                    <div className="mt-4">
                      <h6 className="mb-3 fw-bold">Selected File</h6>
                      <ListGroup>
                        {selectedFiles.map((file, index) => (
                          <ListGroup.Item key={`${file.name}-${index}`} className="d-flex justify-content-between align-items-center">
                            <div>
                              <span className="text-dark fw-500">{file.name}</span>
                              <span className="text-muted ms-2">({(file.size / 1024).toFixed(2)} KB)</span>
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeFile(index)}
                              disabled={isLoading}
                            >
                              Remove
                            </Button>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </div>
                  )}
                </div>

                <div className="col-md-4">
                  <Card className="bg-light border-0">
                    <Card.Body>
                      <h6 className="fw-bold mb-3">Required CSV structure</h6>
                      <ul className="list-unstyled">
                        <li className="mb-2">
                          <small>Header must include servicetag.</small>
                        </li>
                        <li className="mb-2">
                          <small>Each row is validated against the 4-part dot-separated Service Tag format.</small>
                        </li>
                        <li>
                          <small>A single file may contain multiple 12NCs.</small>
                        </li>
                      </ul>
                    </Card.Body>
                  </Card>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <>
                {isLoading && (
                  <div className="text-center py-4">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 mb-0">Preparing preview...</p>
                  </div>
                )}

                {!isLoading && !hasPreviewData && (
                  <div className="alert alert-warning mb-0">
                    Preview data is not available. Please check the selected CSV files and try Preview again.
                  </div>
                )}

                {!isLoading && hasPreviewData && (
                  <>
                    <div className="row mb-2">
                      <div className="col-md-4 mb-2">
                        <Card className="text-center h-100">
                          <Card.Body style={{ padding: '8px 12px' }}>
                            <small className="text-muted mb-1 d-block">Total Service Tags</small>
                            <h5 className="mb-0" style={{ fontSize: '1.25rem' }}>{csvData?.originalCsvData?.length || 0}</h5>
                          </Card.Body>
                        </Card>
                      </div>
                      <div className="col-md-4 mb-2">
                        <Card className="text-center h-100">
                          <Card.Body style={{ padding: '8px 12px' }}>
                            <small className="text-muted mb-1 d-block">Valid Service Tags</small>
                            <h5 className="mb-0" style={{ fontSize: '1.25rem' }}>
                              {csvData?.originalCsvData?.filter(row => row.isValid !== false).length || 0}
                            </h5>
                          </Card.Body>
                        </Card>
                      </div>
                      <div className="col-md-4 mb-2">
                        <Card className="text-center h-100">
                          <Card.Body style={{ padding: '8px 12px' }}>
                            <small className="text-muted mb-1 d-block">Invalid Service Tags</small>
                            <h5 className="mb-0" style={{ fontSize: '1.25rem' }}>
                              {csvData?.originalCsvData?.filter(row => row.isValid === false).length || 0}
                            </h5>
                          </Card.Body>
                        </Card>
                      </div>
                      <div className="col-md-4 mb-2">
                        <Card className="text-center h-100">
                          <Card.Body style={{ padding: '8px 12px' }}>
                            <small className="text-muted mb-1 d-block">12NCs Processed</small>
                            <h5 className="mb-0" style={{ fontSize: '1.25rem' }}>
                              {new Set(csvData?.originalCsvData?.map(row => row.extracted12NC).filter(Boolean)).size}
                            </h5>
                          </Card.Body>
                        </Card>
                      </div>
                      <div className="col-md-4 mb-2">
                        <Card className="text-center h-100">
                          <Card.Body style={{ padding: '8px 12px' }}>
                            <small className="text-muted mb-1 d-block">Factory Codes</small>
                            <h5 className="mb-0" style={{ fontSize: '1.25rem' }}>
                              {new Set(csvData?.originalCsvData?.map(row => row.extractedFactoryCode).filter(Boolean)).size}
                            </h5>
                          </Card.Body>
                        </Card>
                      </div>
                      <div className="col-md-4 mb-2">
                        <Card className="text-center h-100">
                          <Card.Body style={{ padding: '8px 12px' }}>
                            <small className="text-muted mb-1 d-block">Extracted Batch IDs</small>
                            <h5 className="mb-0" style={{ fontSize: '1.25rem' }}>
                              {new Set(csvData?.originalCsvData?.map(row => row.extractedBatchId).filter(Boolean)).size}
                            </h5>
                          </Card.Body>
                        </Card>
                      </div>
                    </div>

                    <h6 className="fw-bold mb-2 mt-2" style={{ fontSize: '0.9rem' }}>Preview of uploaded data:</h6>
                    <BOMDataDisplay
                      bomData={bomData || {}}
                      csvData={csvData}
                      showCreateBOMButton={false}
                      previewOnly={true}
                      uploadedCSVFiles={selectedFiles}
                      supportedFactoryCodes={supportedFactoryCodes}
                      factoryBOMRows={factoryBOMRows}
                    />
                  </>
                )}
              </>
            )}

            {currentStep === 3 && renderStepThreeContent()}
          </Modal.Body>
          <Modal.Footer>
            {currentStep === 1 && (
              <>
                <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
                <Button className="bom-btn-primary" onClick={handlePreview} disabled={selectedFiles.length === 0 || isLoading}>
                  Generate Preview
                </Button>
              </>
            )}

            {currentStep === 2 && (
              <>
                <Button variant="outline-secondary" onClick={() => setCurrentStep(1)} disabled={isSubmittingUpload}>
                  Back
                </Button>
                <Button
                  className="bom-btn-primary"
                  onClick={handleUpload}
                  disabled={isSubmittingUpload || isLoading || !hasPreviewData}
                >
                  {isSubmittingUpload ? 'Uploading...' : 'Upload'}
                </Button>
              </>
            )}

            {currentStep === 3 && (
              <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
            )}
          </Modal.Footer>
        </Modal>
      </Card.Body>
    </Card>
  );
};

export default FileUpload;